import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

interface CatItem { key: string; section: string; }
interface UserRow { id: number; username: string; role: string; tenant_id: number; display_name?: string; }

// Super-admin tool: choose exactly which sidebar menus an admin can see. When a
// user is "restricted", the hidden menus disappear AND their APIs return 403.
export default function MenuAccessPage(_props: { user?: any }) {
  const [catalog, setCatalog] = useState<CatItem[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [sel, setSel] = useState<number | null>(null);
  const [restricted, setRestricted] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState(''); const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [c, u] = await Promise.all([
        axios.get('/api/admin-panel/menu-catalog'),
        axios.get('/api/admin-panel/users'),
      ]);
      setCatalog(c.data?.items || []);
      const list = Array.isArray(u.data) ? u.data : (u.data?.users || []);
      setUsers(list);
    } catch (e: any) { setErr(e?.response?.data?.error || 'Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickUser = async (id: number) => {
    setSel(id); setMsg(''); setErr('');
    try {
      const r = await axios.get(`/api/admin-panel/users/${id}/menu-access`);
      const allowed: string[] | null = r.data?.menu_allowed || null;
      if (allowed && allowed.length) { setRestricted(true); setChecked(new Set(allowed)); }
      else { setRestricted(false); setChecked(new Set()); }
    } catch (e: any) { setErr(e?.response?.data?.error || 'Could not load this user'); }
  };

  const toggle = (k: string) => { const s = new Set(checked); s.has(k) ? s.delete(k) : s.add(k); setChecked(s); };
  const sectionToggle = (items: CatItem[], on: boolean) => {
    const s = new Set(checked); items.forEach((i) => (on ? s.add(i.key) : s.delete(i.key))); setChecked(s);
  };

  const save = async () => {
    if (sel == null) return;
    setSaving(true); setMsg(''); setErr('');
    const body = restricted ? { menu_allowed: Array.from(checked) } : { menu_allowed: null };
    try {
      await axios.put(`/api/admin-panel/users/${sel}/menu-access`, body);
      setMsg(restricted
        ? 'Saved. The user will see only the ticked menus (they must sign in again for it to take effect).'
        : 'Saved. Full menu access restored (the user must sign in again).');
    } catch (e: any) { setErr(e?.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;

  // group catalog by section, preserving first-seen order
  const sections: string[] = [];
  const bySection: Record<string, CatItem[]> = {};
  catalog.forEach((c) => { if (!bySection[c.section]) { bySection[c.section] = []; sections.push(c.section); } bySection[c.section].push(c); });

  const uMatch = (u: UserRow) => (u.username || '').toLowerCase().includes(q.toLowerCase());
  const shownUsers = users.filter(uMatch);

  const wrap: React.CSSProperties = { padding: 24, maxWidth: 1040 };
  const btn: React.CSSProperties = { padding: '10px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
  const input: React.CSSProperties = { padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 14 };
  const chip = (on: boolean): React.CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid ' + (on ? '#6366f1' : '#ddd'), borderRadius: 8, background: on ? '#eef0ff' : '#fff', cursor: 'pointer', fontSize: 14 });

  return (
    <div style={wrap}>
      <h2 style={{ margin: '0 0 4px' }}>Menu Access</h2>
      <p style={{ color: '#666', marginTop: 0 }}>
        Choose exactly which sidebar menus an admin can see. Restricted menus are hidden from their sidebar
        and their pages return “not available” if accessed directly. Changes take effect the next time the user signs in.
      </p>
      {err && <div style={{ background: '#fdecea', color: '#b00', padding: 10, borderRadius: 8, marginBottom: 10 }}>{err}</div>}
      {msg && <div style={{ background: '#e8f5e9', color: '#1b5e20', padding: 10, borderRadius: 8, marginBottom: 10 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ minWidth: 240 }}>
          <input placeholder="Search users…" value={q} onChange={(e) => setQ(e.target.value)} style={{ ...input, width: '100%', marginBottom: 8 }} />
          <div style={{ maxHeight: 520, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
            {shownUsers.map((u) => (
              <div key={u.id} onClick={() => pickUser(u.id)}
                style={{ padding: '9px 12px', cursor: 'pointer', background: sel === u.id ? '#eef0ff' : 'transparent', borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ fontWeight: sel === u.id ? 700 : 500, fontSize: 14 }}>{u.username}</div>
                <div style={{ fontSize: 12, color: '#888' }}>{u.role}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ flex: 1 }}>
          {sel == null ? <div style={{ color: '#999', padding: 20 }}>Select a user to configure their menus.</div> : (
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontWeight: 600, cursor: 'pointer' }}>
                <input type="checkbox" checked={restricted} onChange={(e) => setRestricted(e.target.checked)} style={{ width: 'auto', margin: 0 }} />
                Restrict this user’s menus {restricted ? '' : '(currently: full access for their role)'}
              </label>

              {restricted && sections.map((s) => {
                const items = bySection[s];
                const allOn = items.every((i) => checked.has(i.key));
                return (
                  <div key={s} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#333' }}>{s}</div>
                      <button onClick={() => sectionToggle(items, !allOn)} style={{ ...input, cursor: 'pointer', fontSize: 12, padding: '4px 10px', background: '#fafafa' }}>
                        {allOn ? 'Clear section' : 'Select section'}
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 8 }}>
                      {items.map((i) => {
                        const on = checked.has(i.key);
                        return (
                          <div key={i.key} style={chip(on)} onClick={() => toggle(i.key)}>
                            <input type="checkbox" checked={on} readOnly style={{ width: 'auto', margin: 0 }} />
                            <span>{i.key}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div style={{ marginTop: 18 }}>
                <button style={{ ...btn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
                  {saving ? 'Saving…' : 'Save menu access'}
                </button>
                {restricted && <span style={{ marginLeft: 12, color: '#666', fontSize: 13 }}>{checked.size} menu(s) allowed</span>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
